import { onDocumentReady } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const SEARCH_SELECTOR = '[data-collection-search]';
const PRODUCT_SELECTOR = '[data-collection-product]';
const EMPTY_SELECTOR = '[data-collection-search-empty]';
const BUTTON_SELECTOR = '[data-collection-search-submit]';
const INJECTED_ATTRIBUTE = 'data-collection-search-injected';

// Only fetch the other pages once the term is meaningful enough to avoid
// loading the whole collection while the user is still typing the first letter.
const MIN_REMOTE_TERM_LENGTH = 2;

const sectionTerms = new Map();

// Cache of every product card (across all pages) per section id.
const allCardsCache = new Map();
// In-flight "load all pages" promises per section id.
const allCardsLoading = new Map();

const normalize = (value = '') =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getSection = (element) => element?.closest?.('.shopify-section') ?? null;

const getNormalizedTitle = (card) => {
  const cached = card.dataset.productTitleNormalized;
  const normalized = cached ?? normalize(card.getAttribute('data-product-title') || '');
  card.dataset.productTitleNormalized = normalized;
  return normalized;
};

const getProductId = (card) => card.dataset.productId || card.getAttribute('data-product-id') || '';

const filterSection = (section, term = '') => {
  if (!(section instanceof HTMLElement)) return;

  const resultsList = section.querySelector('results-list');
  if (!resultsList) return;

  const normalizedTerm = normalize(term);
  let visibleCount = 0;

  resultsList.querySelectorAll(PRODUCT_SELECTOR).forEach((card) => {
    if (!(card instanceof HTMLElement)) return;

    const normalizedTitle = getNormalizedTitle(card);
    const matches = normalizedTerm === '' || normalizedTitle.includes(normalizedTerm);
    card.toggleAttribute('hidden', !matches);

    if (matches) visibleCount += 1;
  });

  section.querySelectorAll(EMPTY_SELECTOR).forEach((message) => {
    if (!(message instanceof HTMLElement)) return;
    message.toggleAttribute('hidden', visibleCount !== 0);
  });
};

/**
 * Fetches every page of the collection section and caches the resulting product
 * cards so the search can match products that are not on the current page yet.
 *
 * @param {HTMLElement} section
 * @returns {Promise<HTMLElement[]>}
 */
const loadAllCards = (section) => {
  if (!(section instanceof HTMLElement)) return Promise.resolve([]);

  const sectionKey = section.id;
  if (allCardsCache.has(sectionKey)) return Promise.resolve(allCardsCache.get(sectionKey));
  if (allCardsLoading.has(sectionKey)) return allCardsLoading.get(sectionKey);

  const resultsList = section.querySelector('results-list');
  const grid = resultsList?.querySelector('[ref="grid"]');
  const sectionId = resultsList?.getAttribute('section-id');
  const lastPage = Number(grid?.dataset.lastPage || '1');

  if (!grid || !sectionId || !Number.isFinite(lastPage) || lastPage <= 1) {
    const empty = [];
    allCardsCache.set(sectionKey, empty);
    return Promise.resolve(empty);
  }

  const promise = (async () => {
    const requests = [];

    for (let page = 1; page <= lastPage; page += 1) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(page));
      url.hash = '';

      requests.push(
        sectionRenderer
          .getSectionHTML(sectionId, true, url)
          .then((html) => ({ page, html }))
          .catch(() => null)
      );
    }

    const responses = await Promise.all(requests);
    const cards = [];
    const seen = new Set();

    responses
      .filter(Boolean)
      .sort((a, b) => a.page - b.page)
      .forEach(({ html }) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll(PRODUCT_SELECTOR).forEach((card) => {
          if (!(card instanceof HTMLElement)) return;

          const id = getProductId(card);
          if (id && seen.has(id)) return;
          if (id) seen.add(id);

          cards.push(card);
        });
      });

    allCardsCache.set(sectionKey, cards);
    return cards;
  })();

  allCardsLoading.set(sectionKey, promise);
  promise.finally(() => allCardsLoading.delete(sectionKey));

  return promise;
};

/**
 * Injects the matching cards that live on other pages into the current grid.
 *
 * @param {HTMLElement} section
 * @param {string} term
 */
const injectMatches = (section, term) => {
  const resultsList = section.querySelector('results-list');
  const grid = resultsList?.querySelector('[ref="grid"]');
  const cards = allCardsCache.get(section.id);
  const normalizedTerm = normalize(term);

  if (!grid || !cards || normalizedTerm === '') return;

  const present = new Set();
  grid.querySelectorAll(PRODUCT_SELECTOR).forEach((card) => {
    const id = getProductId(card);
    if (id) present.add(id);
  });

  const fragment = document.createDocumentFragment();

  cards.forEach((card) => {
    const id = getProductId(card);
    if (id && present.has(id)) return;
    if (!getNormalizedTitle(card).includes(normalizedTerm)) return;
    if (id) present.add(id);

    const clone = card.cloneNode(true);
    clone.setAttribute(INJECTED_ATTRIBUTE, '');
    // Keep the injected cards out of the infinite-scroll page bookkeeping.
    clone.removeAttribute('ref');
    fragment.appendChild(clone);
  });

  if (fragment.childNodes.length) grid.appendChild(fragment);
};

const removeInjected = (section) => {
  if (!(section instanceof HTMLElement)) return;
  section.querySelectorAll(`[${INJECTED_ATTRIBUTE}]`).forEach((card) => card.remove());
};

/**
 * Runs the search for a section: instant local filter, then (for meaningful
 * terms) pulls in matches from the other pages of the collection.
 *
 * @param {HTMLElement} section
 * @param {string} term
 */
const runSearch = (section, term) => {
  if (!(section instanceof HTMLElement)) return;

  const normalizedTerm = normalize(term);

  if (normalizedTerm === '') {
    removeInjected(section);
    filterSection(section, '');
    return;
  }

  // Instant feedback using whatever is already rendered.
  filterSection(section, term);

  if (normalizedTerm.length < MIN_REMOTE_TERM_LENGTH) return;

  loadAllCards(section).then(() => {
    // Bail out if the user kept typing while the pages were loading.
    const currentTerm = sectionTerms.get(section.id) ?? '';
    if (normalize(currentTerm) !== normalizedTerm) return;

    injectMatches(section, term);
    filterSection(section, term);
  });
};

const handleInput = (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches(SEARCH_SELECTOR)) return;

  const section = getSection(target);
  if (!section) return;

  sectionTerms.set(section.id, target.value);
  runSearch(section, target.value);
};

const handleButtonClick = (event) => {
  const target = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
  if (!(target instanceof HTMLElement)) return;

  const container = target.closest('[data-collection-search-container]');
  const input = container?.querySelector(SEARCH_SELECTOR);
  if (!(input instanceof HTMLInputElement)) return;

  input.focus();
  const section = getSection(input);
  if (!section) return;

  sectionTerms.set(section.id, input.value);
  runSearch(section, input.value);
};

const bindInputs = () => {
  document.querySelectorAll(SEARCH_SELECTOR).forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.dataset.collectionSearchBound === 'true') return;

    input.dataset.collectionSearchBound = 'true';
    const section = getSection(input);
    const storedValue = section ? sectionTerms.get(section.id) : undefined;

    if (storedValue !== undefined) {
      input.value = storedValue;
    }

    filterSection(section, input.value || '');
  });
};

const clearCaches = () => {
  allCardsCache.clear();
  allCardsLoading.clear();

  document.querySelectorAll('results-list').forEach((resultsList) => {
    const section = getSection(resultsList);
    if (section) removeInjected(section);
  });
};

onDocumentReady(() => {
  bindInputs();
  document.addEventListener('input', handleInput);
  document.addEventListener('search', handleInput);
  document.addEventListener('click', handleButtonClick);
  // The product set changes when facets are applied, so drop the cached pages.
  document.addEventListener(ThemeEvents.FilterUpdate, clearCaches);
});

const observer = new MutationObserver(() => bindInputs());
observer.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', () => {
  clearCaches();
  bindInputs();
});
document.addEventListener('shopify:section:reorder', bindInputs);
document.addEventListener('shopify:section:select', bindInputs);
document.addEventListener('shopify:section:deselect', bindInputs);
